// src/pages/leads/LeadsPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ArrowLeft, ArrowRight, Loader2, Plus, User, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { RowActionMenu } from "@/components/RowActionMenu";
import { hasPerm } from "@/lib/auth";
import { LeadDialog } from "./dialog/LeadDialog";
import { helper } from "@/lib/helper";
import { useTranslation } from "react-i18next";
import { toastError, toastSuccess } from "@/lib/toast";
import { ConfirmDialog } from "@/components/ConfirmDialog";

type Branch = { id: number; name: string };
type Bdo = { id: number; name: string };

export type LeadRow = {
  id: number;
  fname?: string;
  sname?: string;
  phone_number?: string;
  town?: string;

  lead_status?: string;
  account_status?: number | string;

  branch?: number | string; // used by edit dialog prefill
  bdo?: number | string;

  BRANCH?: string;
  USERNAME?: string;
  USERNAME2?: string;
  created_at?: string;
};

type ApiResponse = {
  data: LeadRow[];
  meta: {
    per_page: number;
    next_cursor: string | null;
    prev_cursor: string | null;
    has_more: boolean;
  };
  anchor_id: number | null;
  branches: Branch[];
};

const PER_PAGE = 10;
const API_PATH = "/actions/leads";

const canConvertToCustomer = hasPerm("customers-add");
const canEditLead = hasPerm("lead-edit");
const canCreateLead = hasPerm("add-lead");
const canViewLead = hasPerm("view-lead");

function useDebounced<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function leadStatusLabel(v: LeadRow["lead_status"]) {
  if (v == null) return "—";
  const s = String(v);

  if (s === "Cold") return "Cold";
  if (s === "Warm") return "Warm";
  if (s === "Hot") return "Hot";

  const low = s.toLowerCase();
  if (low.includes("cold")) return "Cold";
  if (low.includes("warm")) return "Warm";
  if (low.includes("hot")) return "Hot";

  return s;
}

function statusPillClass(label: string) {
  const s = label.toLowerCase();
  if (s === "cold") return "bg-amber-500/10 border-amber-500/20 text-amber-700";
  if (s === "warm") return "bg-blue-500/10 border-blue-500/20 text-blue-700";
  if (s === "hot") return "bg-red-500/10 border-red-500/20 text-red-700";
  return "bg-muted border-border text-foreground";
}

export default function LeadsPage() {
  // filters
  const [q, setQ] = useState("");
  const qDebounced = useDebounced(q, 350);
  const [branchId, setBranchId] = useState("");
  const [bdoId, setBdoId] = useState("");

  const { t } = useTranslation();

  // data
  const [loading, setLoading] = useState(false);
  const [bdosLoading, setBdosLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [rows, setRows] = useState<LeadRow[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [bdos, setBdos] = useState<Bdo[]>([]);

  // cursor paging
  const [cursor, setCursor] = useState<string | null>(null);
  const [anchorId, setAnchorId] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [prevCursor, setPrevCursor] = useState<string | null>(null);

  const tableTopRef = useRef<HTMLDivElement | null>(null);
  const scrollToTableTop = () => {
    requestAnimationFrame(() => {
      tableTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const navigate = useNavigate();
  const location = useLocation();
  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const isRTL = dir === "rtl";

  const openCustomerProfile = (id: number | string) => {
    const selectedId = String(id);
    sessionStorage.setItem("selectedCustomer", selectedId);

    navigate("/customer-profile", {
      replace: location.pathname === "/customer-profile",
      state: { selectedCustomer: selectedId, nonce: Date.now() },
    });
  };

  // reset paging when filters change
  useEffect(() => {
    setCursor(null);
    setAnchorId(null);
  }, [qDebounced, branchId, bdoId]);

  // branch-first: load bdos ONLY for selected branch
  useEffect(() => {
    setBdoId("");
    setBdos([]);

    if (!branchId) return;

    let alive = true;
    (async () => {
      setBdosLoading(true);
      try {
        const list = await apiFetch<Bdo[]>(`/actions/leads/bdos/${branchId}`);
        if (!alive) return;
        setBdos(list ?? []);
      } catch {
        if (!alive) return;
        setBdos([]);
      } finally {
        if (!alive) return;
        setBdosLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [branchId]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("per_page", String(PER_PAGE));

    if (qDebounced.trim()) params.set("q", qDebounced.trim());
    if (branchId) params.set("branch_id", branchId);
    if (branchId && bdoId) params.set("bdo_id", bdoId);

    if (cursor) params.set("cursor", cursor);
    if (anchorId != null) params.set("anchor_id", String(anchorId));

    return params.toString();
  }, [qDebounced, branchId, bdoId, cursor, anchorId]);

  async function load() {
    setLoading(true);
    setError(null);

    try {
      const json = await apiFetch<ApiResponse>(`${API_PATH}/all?${queryString}`);

      setRows(json.data ?? []);
      setBranches(json.branches ?? []);

      setNextCursor(json.meta?.next_cursor ?? null);
      setPrevCursor(json.meta?.prev_cursor ?? null);

      setAnchorId((prev) => (prev == null ? json.anchor_id ?? null : prev));
    } catch (e: any) {
      setError(e?.message ?? "Failed to load");
      setRows([]);
      setNextCursor(null);
      setPrevCursor(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  const resetAll = () => {
    setQ("");
    setBranchId("");
    setBdoId("");
    setBdos([]);
    setCursor(null);
    setAnchorId(null);
  };

  const goPrev = () => {
    if (!prevCursor || loading) return;
    setCursor(prevCursor);
    scrollToTableTop();
  };

  const goNext = () => {
    if (!nextCursor || loading) return;
    setCursor(nextCursor);
    scrollToTableTop();
  };

  const nameOf = (r: LeadRow) => {
    const n = `${r.fname ?? ""} ${r.sname ?? ""}`.trim();
    return n || `#${r.id}`;
  };

  const bdoLabel = (r: LeadRow) => (r.USERNAME ?? "-") + (r.USERNAME2 ? ` & ${r.USERNAME2}` : "");

  // dialogs
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [leadDialogMode, setLeadDialogMode] = useState<"create" | "edit">("create");
  const [editingLead, setEditingLead] = useState<LeadRow | null>(null);

  const openCreate = () => {
    setLeadDialogMode("create");
    setEditingLead(null);
    setLeadDialogOpen(true);
  };

  const openEdit = (r: LeadRow) => {
    setLeadDialogMode("edit");
    setEditingLead(r);
    setLeadDialogOpen(true);
  };

  // inside component:
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState<LeadRow | null>(null);

  const openConvertConfirm = (lead: LeadRow) => {
    setSelectedLead(lead);
    setConfirmOpen(true);
  };

  const convertLeadConfirmed = async () => {
    if (!selectedLead) return;

    try {
      setConfirmLoading(true);
      // ✅ open wizard WITHOUT id in URL (pass via state)
      navigate("/actions/new-customer", { state: { customerId: String(selectedLead.id), from: "convert" } });

      setConfirmOpen(false);
      setSelectedLead(null);
      // reload leads list
      // load();
    } catch (e: any) {
      toastError(e?.message ?? "Failed to convert lead");
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 py-4 space-y-4">
      {/* top loading bar */}




      <header className="border-b border-border bg-card sticky top-0 z-30 mb-3">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <Users className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight"> {t("lead.title")}</h1>
              <p className="text-xs text-muted-foreground">Leads listing </p>
            </div>
          </div>

          {canCreateLead && (
            <Button onClick={openCreate} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Lead
            </Button>
          )}
        </div>
      </header>

      <div className={cn("h-1 w-full overflow-hidden rounded-full bg-muted", loading ? "opacity-100" : "opacity-0")}>
        <div className="h-full w-1/3 animate-pulse bg-primary" />
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-3 sm:p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Search</div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name / phone / town..."
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Branch</div>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">All Branches</option>
              {branches.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">BDO</div>
            <select
              value={bdoId}
              onChange={(e) => setBdoId(e.target.value)}
              disabled={!branchId || bdosLoading}
              className={cn(
                "h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                (!branchId || bdosLoading) && "opacity-60 cursor-not-allowed"
              )}
            >
              <option value="">
                {!branchId ? "Select branch first" : bdosLoading ? "Loading BDOs..." : "All BDOs"}
              </option>
              {bdos.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-1">
          <div className="flex flex-wrap gap-2 text-sm">
            <div className="px-3 py-1 rounded-full bg-muted">
              Rows: <span className="font-semibold">{rows.length}</span>
            </div>
            <div className="px-3 py-1 rounded-full bg-muted">
              Anchor: <span className="font-semibold">{anchorId ?? "-"}</span>
            </div>

            {loading && (
              <div className="px-3 py-1 rounded-full bg-muted text-muted-foreground inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading...
              </div>
            )}
          </div>

          <button
            onClick={resetAll}
            className="h-10 px-3 rounded-md border border-border hover:bg-muted text-sm font-medium"
          >
            Reset
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}
      </div>

      {/* Listing */}
      <div className="bg-card border border-border rounded-xl p-3 sm:p-4 relative">
        {/* overlay loader */}
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 backdrop-blur-sm rounded-xl">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading leads...
            </div>
          </div>
        )}

        <div ref={tableTopRef} />

        {/* Desktop table */}
        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Branch</th>
                  <th className="px-4 py-3 font-semibold">BDO</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Town</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  {(canViewLead || canConvertToCustomer || canEditLead) && (
                    <th className="px-4 py-3 font-semibold text-right">Action</th>
                  )}
                </tr>
              </thead>

              <tbody>
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                      No leads found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const st = leadStatusLabel(r.lead_status);
                    return (
                      <tr
                        key={r.id}
                        className="border-t border-border hover:bg-muted/30 cursor-pointer"
                        onClick={() => openCustomerProfile(r.id)}
                      >
                        <td className="px-4 py-3">{r.BRANCH ?? "-"}</td>

                        <td className="px-4 py-3">
                          <div className="font-medium">{r.USERNAME ?? "-"}</div>
                          <div className="text-xs text-muted-foreground">{r.USERNAME2 ?? ""}</div>
                          <div className="text-xs text-muted-foreground">{helper.compactDateTime(r.created_at) ?? ""}</div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="font-medium">{nameOf(r)}</div>
                          <div className="text-xs text-muted-foreground">{r.phone_number ?? "-"}</div>
                        </td>

                        <td className="px-4 py-3">{r.town ?? "-"}</td>

                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              "inline-flex items-center px-2 py-1 rounded-full text-xs border",
                              statusPillClass(st)
                            )}
                          >
                            {st}
                          </span>
                        </td>

                        {(canViewLead || canConvertToCustomer || canEditLead) && (
                          <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <RowActionMenu
                              isRTL={isRTL}
                              onView={() => openCustomerProfile(r.id)}
                              onEdit={canEditLead ? () => openEdit(r) : undefined}
                              onConvert={canConvertToCustomer ? () => openConvertConfirm(r) : undefined}
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-2">
          {!loading && rows.length === 0 ? (
            <div className="rounded-lg border border-border p-5 text-center text-sm text-muted-foreground">
              No leads found.
            </div>
          ) : (
            rows.map((r) => {
              const st = leadStatusLabel(r.lead_status);
              return (
                <div
                  key={r.id}
                  className="rounded-xl border border-border bg-background p-3 cursor-pointer"
                  onClick={() => openCustomerProfile(r.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{nameOf(r)}</div>
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2 gap-y-1 mt-1">
                        <span className="truncate">{r.phone_number ?? "-"}</span>
                        <span>•</span>
                        <span className="truncate">{r.town ?? "-"}</span>

                      </div>

                      <div className="text-xs text-muted-foreground mt-1">
                        {r.BRANCH ?? "-"} • {bdoLabel(r)}
                      </div>


                    </div>

                    <span
                      className={cn(
                        "shrink-0 inline-flex items-center px-2 py-1 rounded-full text-xs border",
                        statusPillClass(st)
                      )}
                    >
                      {st}
                    </span>

                    {(canViewLead || canConvertToCustomer || canEditLead) && (
                      <div className="shrink-0 inline-flex items-center px-2 py-1 rounded-full text-xs border" onClick={(e) => e.stopPropagation()}>
                        <RowActionMenu
                          isRTL={isRTL}
                          onView={() => openCustomerProfile(r.id)}
                          onEdit={canEditLead ? () => openEdit(r) : undefined}
                          onConvert={canConvertToCustomer ? () => openConvertConfirm(r) : undefined}
                        />
                      </div>
                    )}

                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Bottom pagination */}
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            onClick={goPrev}
            disabled={!prevCursor || loading}
            className={cn(
              "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
              !prevCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
            )}
          >
            <ArrowLeft className="h-4 w-4" />
            Prev
          </button>

          <button
            onClick={goNext}
            disabled={!nextCursor || loading}
            className={cn(
              "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
              !nextCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
            )}
          >
            Next
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <LeadDialog
        open={leadDialogOpen}
        onOpenChange={setLeadDialogOpen}
        mode={leadDialogMode}
        branches={branches}
        initial={editingLead}
        onSaved={load}
      />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(v) => !confirmLoading && setConfirmOpen(v)}
        loading={confirmLoading}
        title="Convert lead to customer?"
        description={
          selectedLead
            ? `This will create a customer record for ${selectedLead.fname ?? ""} ${selectedLead.sname ?? ""} and open the Customer Wizard.`
            : "This will create a customer record and open the Customer Wizard."
        }
        confirmText="Convert & Open Wizard"
        cancelText="Cancel"
        onConfirm={convertLeadConfirmed}
      />
    </div>
  );
}